import React, { useState, useEffect } from 'react';
// react-bootstrap
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Spinner from 'react-bootstrap/Spinner';
// components
import Thumb from '../Thumb';
import RatingStars from '../RatingStars';
import ChooseListModal from '../Modals/ChooseListModal';
import UpdatePageModal from '../Modals/UpdatePageModal';
import CircularProgressBar from '../CircularProgressBar';
// hooks
import { useAddDeleteUpdateBook } from '../Hooks/useAddDeleteUpdateBook';
// images
import NoImage from '../../images/book_place_holder.svg';
// helper
import {
  getReadingProgress,
  unMarkBestSellerBook,
  markBestSellerBook,
  isBookFavourite,
  updateUserBookCurrentPage,
  updateBestSellerBookCurrentPage,
} from '../../utils/helpers';
// styles
import { Wrapper } from './BookItemDetails.styles';

const BookItemDetails = ({ book, source }) => {
  const [showMore, setShowMore] = useState(false);
  const [state, setState] = useState({
    book,
    isSaved: false,
    listName: '',
  });

  const [chooseListModalShow, setChooseListModalShow] = useState(false);
  const [updatePageModalShow, setUpdatePageModalShow] = useState(false);

  const { loading, addBookToList, deleteBook, updateBookPage } =
    useAddDeleteUpdateBook();

  const handleAddBookToList = async (book, listName) => {
    const error = await addBookToList(book, listName);
    if (!error) {
      setState((oldState) => ({
        ...oldState,
        isSaved: true,
        listName,
      }));
      markBestSellerBook(book._id, listName);
    }
    setChooseListModalShow(false);
  };

  const handleDeleteBook = async () => {
    const error = await deleteBook(book._id);
    if (!error) {
      setState((oldState) => ({ ...oldState, isSaved: false, listName: '' }));
      unMarkBestSellerBook(book._id);
    }
  };

  const handleUpdateBookPage = async (page) => {
    const error = await updateBookPage(book._id, page);

    if (!error) {
      const updatedBook = { ...state.book, currentPage: page };

      setState((oldState) => ({
        ...oldState,
        book: updatedBook,
      }));

      updateUserBookCurrentPage(book._id, page);
      updateBestSellerBookCurrentPage(book._id, page);
    }
    setUpdatePageModalShow(false);
  };

  useEffect(() => {
    // check description length to trim text
    if (book.description?.length < 400) {
      setShowMore(true);
    }
    // check if book saved when source = discover
    if (source === 'discover') {
      const savedBook = isBookFavourite(book._id);
      if (savedBook) {
        setState((oldState) => ({
          ...oldState,
          book: savedBook,
          isSaved: true,
          listName: savedBook.listName,
        }));
      }
    }
    // check if book saved
    if (book.listName) {
      setState((oldState) => ({
        ...oldState,
        isSaved: true,
        listName: book.listName,
      }));
    }
  }, [book.description, book.listName, book._id, source]);

  return (
    <Wrapper>
      <Container className='my-5'>
        {loading && (
          <div className={'my-5 text-center'}>
            <Spinner
              animation='border'
              variant='primary'
              className={'text-center'}
            />
          </div>
        )}
        <Row>
          <Col
            xs={{ span: 8, order: 'first' }}
            md={{ span: 4, order: 'first' }}
            lg={{ span: 4, order: 'first' }}
            className={'mt-5'}
          >
            <Thumb image={book.thumbnail ? book.thumbnail : NoImage} />
          </Col>

          <Col
            xs={{ span: 12, order: 'last' }}
            md={{ span: 6, order: 1 }}
            lg={{ span: 6, order: 1 }}
            className={'mt-5'}
          >
            <h1 className='title'>{book.title}</h1>
            <h3 className='author'>{book.authors[0]}</h3>
            <p className='description mt-3'>
              {showMore
                ? book.description
                : `${book.description.slice(0, 400)}... `}
            </p>
            {!showMore && (
              <div className='text-center'>
                <button
                  className='show-more-btn'
                  onClick={() => setShowMore(true)}
                >
                  <i class='bi bi-chevron-down'></i>
                </button>
              </div>
            )}

            <Row className='mt-5'>
              <Col xs={4} md={4} lg={4}>
                <h4 className='title'>Publish Date</h4>
              </Col>
              <Col xs={8} md={8} lg={8}>
                <h3 className='info'>{state.book.publishedDate}</h3>
              </Col>
            </Row>

            <Row className='mt-2'>
              <Col xs={4} md={4} lg={4}>
                <h4 className='title'>Total Pages</h4>
              </Col>
              <Col xs={8} md={8} lg={8}>
                <h3 className='info'>{state.book.totalPages}</h3>
              </Col>
            </Row>

            {state.book.listName === 'reading' && (
              <Row className='mt-2'>
                <Col xs={4} md={4} lg={4}>
                  <h4 className='title'>Current Page</h4>
                </Col>
                <Col xs={2} md={2} lg={2}>
                  <h3 className='info'>{state.book.currentPage}</h3>
                </Col>
                <Col xs={2} md={2} lg={2}>
                  <CircularProgressBar
                    sqSize={60}
                    strokeWidth={4}
                    percentage={getReadingProgress(
                      state.book.currentPage,
                      state.book.totalPages
                    )}
                  />
                </Col>
                <Col xs={4} md={4} lg={4}>
                  <button
                    className='edit-btn mt-2'
                    onClick={() => setUpdatePageModalShow(true)}
                  >
                    <i class='bi bi-pencil pe-2'></i> Edit
                  </button>
                </Col>
              </Row>
            )}

            <Row className='mt-2'>
              <Col xs={4} md={4} lg={4}>
                <h4 className='title'>Rating</h4>
              </Col>
              <Col xs={8} md={8} lg={8}>
                <RatingStars rating={state.book.averageRating} />
              </Col>
            </Row>
          </Col>
          <Col
            xs={{ span: 4, order: 2 }}
            md={{ span: 2, order: 2 }}
            lg={{ span: 2, order: 2 }}
            className={'mt-5'}
          >
            <OverlayTrigger
              placement='bottom'
              overlay={
                state.isSaved ? (
                  <Tooltip>remove book from list</Tooltip>
                ) : (
                  <Tooltip>add book to bookmarks</Tooltip>
                )
              }
            >
              <button
                className='bookmark'
                onClick={() => {
                  if (state.isSaved) handleDeleteBook(book._id);
                  else setChooseListModalShow(true);
                }}
              >
                {state.isSaved ? (
                  <i class='bi bi-bookmark-heart-fill'></i>
                ) : (
                  <i class='bi bi-bookmark-heart'></i>
                )}
              </button>
            </OverlayTrigger>
            <h3 className='ms-2' style={{ color: 'var(--brandSecondary)' }}>
              {state.listName && state.listName.replaceAll('_', ' ')}
            </h3>
          </Col>
        </Row>
        <ChooseListModal
          show={chooseListModalShow}
          onHide={() => setChooseListModalShow(false)}
          book={state.book}
          addBookToList={handleAddBookToList}
        />
        <UpdatePageModal
          show={updatePageModalShow}
          onHide={() => setUpdatePageModalShow(false)}
          book={state.book}
          updateBook={handleUpdateBookPage}
        />
      </Container>
    </Wrapper>
  );
};

export default BookItemDetails;
